/**
 * Scheduled passes: the recurrence rule, in the words a school reads it in
 * (ENG-9924).
 *
 * Split out of the page because the RULE is the testable part and the cards
 * around it are not — the same split `passTypeIssuers.ts` makes for the issuer
 * allowlist. It is also the part most likely to be wrong: "every other Tuesday"
 * is the sentence a leader checks before trusting the schedule, and a summary
 * that renders it as "Weekly on Tue" is a silent lie about when a student
 * leaves class.
 *
 * ⚠️ MOCK-ONLY for now. Campus has no schedule storage (ENG-9951), so nothing
 * here is persisted and `nextOccurrence` walks forward from the device clock
 * rather than the school's timezone. A real build reads the school's wall clock
 * the way `noPassWindows.ts` does.
 */

/** `0` = Sunday … `6` = Saturday, matching `NoPassWindow.days`. */
export const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

/**
 * The order weekdays are READ in, Monday first.
 *
 * The stored values keep `Date.getDay()`'s Sunday-zero numbering, because
 * every other schedule in the product does. But a school reads a week as
 * M T W T F S S: a Sunday-first row puts the two days a pull-out never happens
 * on at opposite ends and pushes the working week off centre.
 */
export const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** Sorts a day set into reading order, Monday first. */
export function orderDays(days: readonly number[]): number[] {
  return [...days].sort((a, b) => DAY_ORDER.indexOf(a as never) - DAY_ORDER.indexOf(b as never));
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export type RepeatMode = 'once' | 'weekly' | 'biweekly';

/** One stored schedule, as the mock page holds it. */
export interface ScheduledPass {
  id: string;
  /** One schedule can cover a GROUP — a speech pull-out of four is one
   *  appointment, not four. Each student still gets their own occurrence, so
   *  one can take it and another can miss it. */
  studentNames: string[];
  /** Campus student ids, when the schedule was made with the picker. Seeded
   *  mock rows have none — they are personas, not roster entries. */
  studentIds: string[];
  /**
   * Which grades and rosters the students on this schedule belong to.
   *
   * Carried ON the row because the grade/roster filter has to match something
   * and this mock has no student records to look membership up from. A real
   * build (ENG-9951) filters on live membership instead, and these two fields
   * go away with the rest of the mock state.
   */
  grades: string[];
  rosters: string[];
  passTypeName: string;
  /** Weekdays it repeats on. EMPTY means a one-off. */
  days: number[];
  /** 1 = weekly, 2 = every other week. */
  everyWeeks: number;
  /** `HH:mm`, wall clock: when the pass becomes available. */
  time: string;
  /** `HH:mm`: the last moment it can be started. After this it is missed. */
  endTime: string;
  note: string;
  /** May cross a no-pass window — the medical case. */
  immediateAccess: boolean;
  /** `YYYY-MM-DD`. The first day the rule can fire. */
  startsOn: string;
  /**
   * How the series ends, matching `ShoppingPeriodRecurrence.end_kind`
   * (ENG-9699). "Until [blank]" could not tell a school whether the schedule
   * ran forever or was half-filled in; these three name the answer.
   */
  endKind: EndKind;
  /** `endKind: 'on'` — last day it can fire, inclusive. */
  endsOn: string;
  /** `endKind: 'after'` — stop after this many times. */
  endCount: number;
  paused: boolean;
}

export type EndKind = 'never' | 'on' | 'after';

/** What the editor holds while it is open. */
export interface ScheduleDraft {
  studentIds: string[];
  passTypeName: string;
  repeat: RepeatMode;
  days: number[];
  time: string;
  endTime: string;
  note: string;
  immediateAccess: boolean;
  startsOn: string;
  endKind: EndKind;
  endsOn: string;
  endCount: string;
}

/**
 * The students on a schedule, in a row's worth of space.
 *
 * One name plus a count, matching the Pass managers button: a school checks the
 * first name to recognise the appointment, and the count tells them it is a
 * group without spending the rest of the line on it. "4 students" alone would
 * be unrecognisable in a list of a dozen pull-outs.
 */
export function studentSummary(names: string[]): string {
  if (names.length === 0) return 'No students';
  if (names.length === 1) return names[0];
  return `${names[0]} & ${names.length - 1} more`;
}

/** Minutes from one wall-clock time to another, same day. */
export function minutesBetween(from: string, to: string): number {
  const [fh, fm] = from.split(':').map(Number);
  const [th, tm] = to.split(':').map(Number);
  if (![fh, fm, th, tm].every(Number.isFinite)) return 0;
  return th * 60 + tm - (fh * 60 + fm);
}

/** `11:30` + 10 → `11:40`. Wraps at midnight rather than producing `24:10`. */
export function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Whether the window closes after it opens. A pass available 11:30–11:20 is
 *  a schedule that can never be taken, and nothing downstream would say so. */
export function isWindowValid(from: string, to: string): boolean {
  return to > from;
}

/** `2026-09-08` → `Sep 8`. Bare `YYYY-MM-DD` reads as a database value. */
export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[m - 1]} ${d}`;
}

/**
 * How long the series runs, as a phrase to hang off the pattern.
 *
 * Deliberately says nothing at all for the common case — a schedule that
 * starts today and never ends. A row reading "Weekdays at 11:30 AM · from Sep 8
 * · never ends" spends two thirds of its width on the least surprising thing
 * about it.
 */
export function rangeSummary(
  row: Pick<ScheduledPass, 'startsOn' | 'endKind' | 'endsOn' | 'endCount'>,
  today = '',
): string {
  const parts: string[] = [];
  if (row.startsOn && row.startsOn > today) parts.push(`from ${formatDate(row.startsOn)}`);
  if (row.endKind === 'on' && row.endsOn) parts.push(`until ${formatDate(row.endsOn)}`);
  if (row.endKind === 'after') parts.push(`${row.endCount} times`);
  return parts.join(', ');
}

/** `09:15` → `9:15 AM`. Leading zeros read as machine output on a settings row. */
export function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const suffix = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * The pattern as a sentence: "Weekdays at 11:30 AM", "Every other Tue at 9:15 AM".
 *
 * "Weekdays" rather than "Mon, Tue, Wed, Thu, Fri" because that is the one
 * pattern a school sets most and the long form pushes everything else off the
 * row. Weekends are not collapsed the same way: "Sat, Sun" is rare enough that
 * naming it is clearer than a word for it.
 */
export function patternSummary(
  row: Pick<ScheduledPass, 'days' | 'everyWeeks' | 'time'> & Partial<Pick<ScheduledPass, 'startsOn'>>,
): string {
  const at = `at ${formatTime(row.time)}`;
  // A one-off carries its DATE here, because it is the only place left to say
  // it: there is no recurrence to describe and no "next" to print, so a row
  // reading "Once at 1:15 PM" names a pass with no day attached.
  if (row.days.length === 0) {
    return row.startsOn ? `Once on ${formatDate(row.startsOn)} ${at}` : `Once ${at}`;
  }

  // Reading order, so the summary and the chips agree.
  const sorted = orderDays(row.days);
  const isWeekdays = sorted.join() === '1,2,3,4,5';
  const isEveryDay = sorted.length === 7;
  const dayText = isEveryDay
    ? 'Every day'
    : isWeekdays
      ? 'Weekdays'
      : sorted.map((d) => DAY_NAMES[d]).join(', ');

  // The interval leads, because it is the part that changes what a reader
  // expects: "Every other Tue" and "Tue" describe different weeks.
  if (row.everyWeeks === 2) return `Every other ${dayText.toLowerCase()} ${at}`;
  if (row.everyWeeks > 2) return `Every ${row.everyWeeks} weeks on ${dayText.toLowerCase()} ${at}`;
  return `${dayText} ${at}`;
}

/**
 * The next date this schedule comes round, as a short label.
 *
 * Answers the question a summary cannot: a school reading "Every other Tue"
 * still does not know whether that means this week. Mock-grade — it walks
 * forward from `from` and ignores which week an interval is anchored to.
 *
 * EMPTY for a one-off, and callers must skip the clause rather than print it:
 * "next" is a question about a repeat, and a one-off's date is already in its
 * pattern. Returning a word here is what produced "next once".
 */
export function nextOccurrence(
  row: Pick<ScheduledPass, 'days' | 'time'>,
  from: Date = new Date(),
): string {
  if (row.days.length === 0) return '';

  for (let i = 0; i < 8; i += 1) {
    const day = new Date(from);
    day.setDate(day.getDate() + i);
    if (!row.days.includes(day.getDay())) continue;
    if (i === 0) return 'today';
    if (i === 1) return 'tomorrow';
    return DAY_NAMES[day.getDay()];
  }
  return '—';
}

// ---------------------------------------------------------------------------
// History: what actually happened on a schedule.
// ---------------------------------------------------------------------------

/**
 * What became of one dated occurrence.
 *
 * Only the two SETTLED outcomes. An occurrence that is due later today has not
 * happened yet, and listing it in a history would make "missed" mean two
 * different things — not taken, and not taken *yet* — which is exactly the
 * distinction the whole model exists to keep.
 */
export type OccurrenceState = 'taken' | 'missed';

/** One dated occurrence of a schedule, per student. */
export interface ScheduleOccurrence {
  id: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** Whose occurrence. A group schedule produces one per student. */
  studentName: string;
  state: OccurrenceState;
  /** `HH:mm` the pass was actually started. Empty when missed. */
  startedAt: string;
  /** Minutes the student was out. `0` when missed. */
  minutes: number;
}

/** Taken / missed counts for a set of occurrences. */
export function tallyOccurrences(rows: readonly ScheduleOccurrence[]): {
  taken: number;
  missed: number;
} {
  return {
    taken: rows.filter((o) => o.state === 'taken').length,
    missed: rows.filter((o) => o.state === 'missed').length,
  };
}

/**
 * The history as a sentence: "14 taken, 3 missed since Aug 24".
 *
 * The tally LEADS and the list is evidence, because the question a schedule's
 * history answers is not "what happened on the 8th" but "is this working" — and
 * a ratio answers that in one line where twenty dated rows do not. This is the
 * read that makes a missed occurrence worth recording at all.
 *
 * Missed is named even at zero. "14 taken since Aug 24" alone leaves a reader
 * to infer the absence, and an unbroken record is the most reassuring thing a
 * medical schedule can say, so it should be stated rather than implied.
 */
export function historySummary(
  rows: readonly ScheduleOccurrence[],
  startsOn = '',
): string {
  const { taken, missed } = tallyOccurrences(rows);
  if (taken + missed === 0) {
    return startsOn ? `Nothing yet since ${formatDate(startsOn)}` : 'Nothing yet';
  }
  const counts = `${taken} taken, ${missed} missed`;
  return startsOn ? `${counts} since ${formatDate(startsOn)}` : counts;
}

// ---------------------------------------------------------------------------
// The grade / roster filter.
// ---------------------------------------------------------------------------

/**
 * Filter token ids, namespaced by dimension.
 *
 * A school with a roster called "Grade 6" alongside the grade 6 list is not
 * unusual, and an unnamespaced id would silently make selecting one select the
 * other.
 */
export const gradeFilterId = (name: string): string => `grade:${name}`;
export const rosterFilterId = (name: string): string => `roster:${name}`;

/**
 * Does this schedule survive the current group filter?
 *
 * One token at a time — `''` for every student, otherwise a single grade or
 * roster. A multi-select was tried first and was more control than the question
 * needs: a pull-out list is read one group at a time, and the combinations a
 * multi-select buys are ones nobody asks for.
 */
export function matchesGroupFilter(
  row: Pick<ScheduledPass, 'grades' | 'rosters'>,
  token: string,
): boolean {
  if (!token) return true;
  return (
    row.grades.some((g) => gradeFilterId(g) === token) ||
    row.rosters.some((r) => rosterFilterId(r) === token)
  );
}

// ---------------------------------------------------------------------------
// One student's schedules.
// ---------------------------------------------------------------------------

/**
 * The schedules a given student is on.
 *
 * Matched on ID rather than on name, because a group schedule names four people
 * and two of them can share a first name. A schedule carries `studentIds` only
 * when it was made with the picker (or seeded against a known student), so a
 * persona row with no ids belongs to nobody's plan — which is the honest answer
 * rather than a guess made from the display name.
 *
 * Order is preserved: the caller decides how to sort, and the plan's lens shows
 * these in the order the owning page holds them.
 */
export function schedulesForStudent(
  rows: readonly ScheduledPass[],
  studentId: string,
): ScheduledPass[] {
  if (!studentId) return [];
  return rows.filter((row) => row.studentIds.includes(studentId));
}

/**
 * How many schedules a student is on, PAUSED ONES INCLUDED.
 *
 * A paused rule is still set up — it comes back next week — so a count that
 * skipped it would tell a school "nothing is scheduled" for a student whose
 * counseling resumes on Monday.
 */
export function scheduledCountForStudent(
  rows: readonly ScheduledPass[],
  studentId: string,
): number {
  return schedulesForStudent(rows, studentId).length;
}

/**
 * Running or paused, for the status filter.
 *
 * `''` keeps both. Separate from the group filter rather than a fourth option
 * inside it: "6th Grade" and "Paused" answer different questions, and a school
 * checking what it has switched off wants that answer across every grade.
 */
export function matchesStatusFilter(row: Pick<ScheduledPass, 'paused'>, status: string): boolean {
  if (!status) return true;
  return status === 'paused' ? row.paused : !row.paused;
}

// ---------------------------------------------------------------------------
// Export.
// ---------------------------------------------------------------------------

/** RFC-4180 quoting, matching `studentIdExportCsv`. */
function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/**
 * One schedule's history as a CSV, for the school that wants it in a sheet.
 *
 * Carries the pass type on every row, not just in the filename: these files get
 * renamed, merged and mailed on, and a column of dates with no idea what they
 * are about is the state a downloaded export most often arrives in.
 *
 * Dates stay ISO and times stay 24-hour — this is the machine-readable copy,
 * and `Sep 3` sorts as text where `2026-09-03` sorts as a date. A missed
 * occurrence leaves the time and duration cells EMPTY rather than writing 0,
 * which would average into a duration report as a zero-minute trip.
 */
export function buildHistoryCsv(
  rows: readonly ScheduleOccurrence[],
  passTypeName: string,
): string {
  const header = ['Date', 'Student', 'Pass type', 'Outcome', 'Started', 'Minutes out'];
  const body = rows.map((o) => [
    o.date,
    o.studentName,
    passTypeName,
    o.state === 'taken' ? 'Taken' : 'Missed',
    o.state === 'taken' ? o.startedAt : '',
    o.state === 'taken' ? String(o.minutes) : '',
  ]);
  return [header, ...body].map((cells) => cells.map(escapeCsvCell).join(',')).join('\r\n');
}

/** `Clinic` + `2026-09-03` → `clinic-schedule-history-2026-09-03.csv`. */
export function historyCsvFileName(passTypeName: string, today: string): string {
  const slug = passTypeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'scheduled-pass'}-history-${today}.csv`;
}
